/**
 * EmployeeLoadingFallback — picks the right loading view for the current
 * employee-app route.
 *
 * - On the HomeView routes (`/user`, `/user/sparks`, `/user/sparks/upcoming`,
 *   `/user/sparks/complete`) → render `EmployeeAppSkeleton`, which previews
 *   HomeView's header + tabs + spark cards.
 *
 * - On every other employee route (`/user/wallet`, `/user/profile`,
 *   `/user/help`, spark detail, etc.) → render a plain centred
 *   `PageLoading`. The HomeView-shaped skeleton would be misleading on
 *   those screens (no tabs, different layout), so we fall back to the
 *   smaller, layout-agnostic spinner instead.
 *
 * Use this anywhere we previously hard-coded `<EmployeeAppSkeleton />` as a
 * loading view: `AppRouter` `/user` Suspense fallback, `EmployeeRouter`
 * Suspense fallback, and `ConditionalRoute` (employee context).
 */
import { FC } from 'react';
import { useLocation } from 'react-router-dom';

import PageLoading from '@components/layout/PageLoading';

import { useAppBodyClasses } from '@hooks/AppHooks/useAppBodyClasses';

import EmployeeAppSkeleton from './EmployeeAppSkeleton';
import { isEmployeeHomeRoute } from './isEmployeeHomeRoute';

const EmployeeLoadingFallback: FC = () => {
    const { pathname } = useLocation();
    // The global admin <AppHeader> (logo + admin notification bell) is rendered
    // unconditionally by AppRouter and only becomes invisible once
    // `body.has-app-header-mobile` is set — a class that <AppHeaderMobile>
    // adds when HomeView (and friends) mount. While we're still in the
    // Suspense fallback, that class is missing and the admin header flashes
    // above the skeleton. Mirror the AppHeaderMobile body-class side effect
    // here so the existing CSS rule in core/app/App.scss hides
    // `.app-header` for the entire loading window.
    useAppBodyClasses(['has-app-header-mobile'], []);
    if (isEmployeeHomeRoute(pathname)) {
        return <EmployeeAppSkeleton />;
    }
    return <PageLoading />;
};

export default EmployeeLoadingFallback;
